import { IExpressionResolution, ParsingErrorKind } from 'app/model/expressions/expression-resolution';

// Different kinds of argument
export type ArgumentKind = 'StringLiteral' | 'BoolLiteral' | 'FloatLiteral' | 'IntegerLiteral' | 'Array' | 'Object' | 'Expression' | 'Unidentified' | 'Null';

/**
 * Defines a parsed function argument.
 */
export interface IArgument {
    /**
     * Argument lexical type.
     */
    kind: ArgumentKind;

    /**
     * Argument value.
     */
    stringValue: string;

    /**
     * The expression resolution in case the argument was an expression (such as: add(1,2)).
     */
    expressionResolution?: IExpressionResolution;

    /**
     * The sub arguments in case of arrays, e.g.: first([1, 2, 3]).
     */
    elements?: IArgument[];
}

/**
 * Parses expression function argument list and determines their types. Supports expressions too.
 */
export class FunctionArgumentParser {
    private start = 0;
    private argumentList: IArgument[] = [];
    private functionArguments: string;

    /**
     * Initializes a new instance of the FunctionArgumentParser class.
     * @param offset The parameters offset from the beginning of the main expression. e.g add(1,2) has an offset of 4.
     * @param resolveExpression A callback invokes to resolve expressions as the parser encounters them.
     */
    constructor(private readonly offset: number, private resolveExpression: (expression: string) => IExpressionResolution) {
    }

    /**
     * Parses an argument string of an expression function.
     * @param funcArgs The function argument string.
     * @param isArrayArgs Indicates if the arguments being parsed belong to an array.
     */
    public parse(funcArgs: string, isArrayArgs: boolean = false): IArgument[] {

        // Don't trim whitespace since the whitespace belongs to the function arguments and
        // it might indicate an error in the expression. In addition, trimming will cause the
        // parsing error position to point to the wrong character place.
        this.functionArguments = funcArgs ? funcArgs : '';
        this.start = 0;
        this.argumentList = [];

        while (this.functionArguments.length > this.start) {
            this.skipWhiteSpaces();
            const firstChar = this.functionArguments.charAt(this.start);

            switch (firstChar) {
                case '\'':
                    this.handleStringArgument();
                    break;
                case '[':
                    this.handleArrayArgument();
                    break;
                case '{':
                    this.handleObjectArgument();
                    break;
                default:
                    // the other remaining options are either a number or a boolean literals or expressions. Extract a
                    // substring from start until the first comma or until the end of the argument list
                    const currentArgEndIndex = this.currentArgumentEndIndex();
                    const argumentString = this.functionArguments.substring(this.start, currentArgEndIndex);
                    const cleanedArgumentString = argumentString.trim();
                    const lowerCaseArgumentString = cleanedArgumentString.toLowerCase();

                    if (lowerCaseArgumentString === 'null') {
                        // found literal null
                        this.argumentList.push({
                            kind: 'Null',
                            stringValue: cleanedArgumentString
                        });
                    } else if (['false', 'true'].includes(lowerCaseArgumentString)) {
                        // found a boolean literal
                        this.argumentList.push({
                            kind: 'BoolLiteral',
                            stringValue: cleanedArgumentString
                        });
                    } else if (!isNaN(Number(cleanedArgumentString))) {

                        if (cleanedArgumentString.includes('.')) {
                            this.argumentList.push({
                                kind: 'FloatLiteral',
                                stringValue: cleanedArgumentString
                            });
                        } else {
                            // found a number literal
                            this.argumentList.push({
                                kind: 'IntegerLiteral',
                                stringValue: cleanedArgumentString
                            });
                        }
                    } else {
                        // check if this is an expression
                        const expressionResolution = this.resolveExpression(argumentString);

                        if (expressionResolution.isValid) {
                            // found an expression
                            this.argumentList.push({
                                kind: 'Expression',
                                stringValue: cleanedArgumentString,
                                expressionResolution
                            });
                        } else {
                            throw ({
                                error: ParsingErrorKind.UnrecognizedToken,
                                startIndex: this.start + this.offset,
                                endIndex: currentArgEndIndex - 1 + this.offset,
                                resolution: expressionResolution
                            });
                        }
                    }

                    // move the start cursor to the next comma index or the end of the argument string if no comma was there
                    this.start = currentArgEndIndex;
            }

            this.skipWhiteSpaces();

            if (!isArrayArgs && this.functionArguments.length > this.start) {
                // we are not done yet, there should be a comma seprator before the next argument
                if (this.functionArguments.charAt(this.start) === ',') {
                    const commaIndex = this.start;

                    // ignore the comma
                    this.start++;
                    this.skipWhiteSpaces();

                    if (this.start >= this.functionArguments.length) {
                        throw ({
                            error: ParsingErrorKind.ExtraTrailingComma,
                            startIndex: commaIndex + this.offset
                        });
                    }
                } else {
                    throw ({
                        error: ParsingErrorKind.MissingComma,
                        startIndex: this.start - 1 + this.offset
                    });
                }
            }
        }

        return this.argumentList;
    }

    private currentArgumentEndIndex(): number {
        let argEndIndex = this.start;
        let openingParanthesisCounter = 0;

        while (argEndIndex < this.functionArguments.length) {
            const currentChar = this.functionArguments.charAt(argEndIndex);

            if (currentChar === ',' && openingParanthesisCounter <= 0) {
                // encountered a comma at the top level, argument ends here
                return argEndIndex;
            } else if (currentChar === '(') {
                openingParanthesisCounter++;

            } else if (currentChar === ')') {
                openingParanthesisCounter--;
            }

            argEndIndex++;
        }

        return this.functionArguments.length;
    }

    private skipWhiteSpaces(): void {
        const skippableChars = /^\s$/;
        while (this.start < this.functionArguments.length &&
               skippableChars.test(this.functionArguments.charAt(this.start))) {
            this.start++;
        }
    }

    private handleStringArgument(): void {
        let index = this.start;

        // find the closing ' (handle \')
        while (true) {
            const closingQuoteIndex = this.functionArguments.indexOf('\'', index + 1);

            if (closingQuoteIndex < 0) {
                // missing closing quote
                throw ({
                    error: ParsingErrorKind.MissingClosingQuote,
                    startIndex: this.start + this.offset
                });
            }

            if (this.functionArguments.charAt(closingQuoteIndex + 1) === '\'') {
                // this is an escaped quote ('') which is part of the string, ignore it
                index = closingQuoteIndex + 1;
            } else {
                // found the closing quote, extract the string literal and add it to the parameter list
                this.argumentList.push({
                    kind: 'StringLiteral',
                    stringValue: this.functionArguments.substring(this.start, closingQuoteIndex + 1)
                });

                this.start = closingQuoteIndex + 1;
                break;
            }
        }
    }

    private handleArrayArgument(): void {
        const closingBracketIndex = this.locateEnclosingElement('[', ']');

        if (closingBracketIndex < 0) {
            throw {
                error: ParsingErrorKind.MissingClosingBracket,
                startIndex: this.start + this.offset
            };
        } else {
            // parse the array elements
            const arrayElementsString = this.functionArguments.substring(this.start + 1, closingBracketIndex);
            const arrayElements: IArgument[] = new FunctionArgumentParser(this.offset + this.start + 1, this.resolveExpression).parse(arrayElementsString);

            this.argumentList.push({
                kind: 'Array',
                stringValue: this.functionArguments.substring(this.start, closingBracketIndex + 1),
                elements: arrayElements
            });

            this.start = closingBracketIndex + 1;
        }
    }

    private handleObjectArgument(): void {
        const closingBraceIndex = this.locateEnclosingElement('{', '}');

        if (closingBraceIndex < 0) {
            throw {
                error: ParsingErrorKind.MissingClosingBrace,
                startIndex: this.start + this.offset
            };
        } else {
            // parse the object
            try {
                JSON.parse(this.functionArguments.substring(this.start, closingBraceIndex + 1));

                this.argumentList.push({
                    kind: 'Object',
                    stringValue: this.functionArguments.substring(this.start, closingBraceIndex + 1)
                });

                this.start = closingBraceIndex + 1;
            } catch (error) {
                throw {
                    error: ParsingErrorKind.InvalidJsonSyntax,
                    startIndex: this.start + this.offset,
                    endIndex: closingBraceIndex + this.offset,
                    innerException: error
                };
            }
        }
    }

    /**
     * Finds the closing element at the corrected nesting level for an opening element. Could be used with array and objects from instance.
     * @param openingElement The element opening char.
     * @param closingElement The element closing char.
     * @returns The index where the closing element was found or -1 if not found.
     */
    private locateEnclosingElement(openingElement: string, closingElement: string): number {
        let openingElementCounter = 0;
        let isInsideStringLiteral = false;
        let closingElementIndex = -1;

        for (let index = this.start; index < this.functionArguments.length; ++index) {
            const currentChar = this.functionArguments.charAt(index);

            switch (currentChar) {
                case openingElement:
                    if (!isInsideStringLiteral) {
                        openingElementCounter++;
                    }

                    break;
                case closingElement:
                    if (!isInsideStringLiteral) {
                        openingElementCounter--;
                    }

                    break;
                case '\'':
                    if (this.functionArguments.charAt(index + 1) !== '\'') {
                        // only flip the switch if this was a real single quote rather than an escaped one
                        isInsideStringLiteral = !isInsideStringLiteral;
                    } else {
                        // Skip adjacent single quote.
                        index++;
                    }
                    break;
                default:
                    break;
            }

            if (openingElementCounter <= 0) {
                closingElementIndex = index;
                break;
            }
        }

        return closingElementIndex;
    }
}
